// ============================================================
// LINE OF SIGHT - what a shot crosses on its way.
//
// Every solid on the map is convex, so "does this segment enter that
// solid" is a clipping problem, not an intersection search: walk the
// face planes, keep the interval of the segment that is inside all of
// them, and see whether anything survives. About twenty lines, exact,
// and - because the segment is three-dimensional - elevation-aware for
// free. Standing on a crate really does let you shoot over a wall.
//
// Pure. Zero React dependency.
// ============================================================
import type { Cover } from '../../types';
import type { Geometry, Solid, Vec3 } from './types';
import { solidGeometry, boundingBox, dot, sub, solidTransparency } from './solids';
import { deriveCoverLevel, DEFAULT_TARGET_HEIGHT } from './coverModel';

/** Cover levels from least to most, so "the highest crossed" is well defined. */
export const COVER_ORDER: Record<Cover['level'], number> = {
  none: 0,
  low: 1,
  half: 2,
  threeQuarter: 3,
  full: 4,
};

const EPSILON = 1e-9;

/** Is this point inside the (convex) solid? */
export function containsPoint(geometry: Geometry, p: Vec3): boolean {
  for (const face of geometry.faces) {
    const p0 = geometry.vertices[face.indices[0]];
    if (dot(face.normal, sub(p, p0)) > EPSILON) return false;
  }
  return true;
}

export interface SegmentHit {
  /** Where along a→b the segment enters, in [0, 1]. */
  tMin: number;
  /** Where it leaves. */
  tMax: number;
}

/**
 * The portion of segment a→b that lies inside the solid, or null.
 *
 * Each face plane cuts the parameter range in half: a plane the segment
 * runs INTO trims the start, one it runs OUT of trims the end. When the
 * two ends cross, the segment misses. A segment parallel to a plane and
 * on its outer side misses immediately - the case that would otherwise
 * divide by zero.
 */
export function segmentHitsSolid(a: Vec3, b: Vec3, geometry: Geometry): SegmentHit | null {
  const d = sub(b, a);
  const span: SegmentHit = { tMin: 0, tMax: 1 };
  for (const face of geometry.faces) {
    if (!clipByFace(face, geometry.vertices[face.indices[0]], a, d, span)) return null;
  }
  return span;
}

/**
 * Narrow the segment's inside interval by one face's plane, in place.
 * False when the segment cannot be inside the solid at all.
 */
function clipByFace(face: Geometry['faces'][number], p0: Vec3, a: Vec3, d: Vec3, span: SegmentHit): boolean {
  const denom = dot(face.normal, d);
  const dist = dot(face.normal, sub(a, p0)); // positive: outside this plane
  // Parallel: inside this plane for its whole length, or never inside.
  if (Math.abs(denom) < EPSILON) return !(dist > EPSILON);
  const t = -dist / denom;
  if (denom < 0)
    span.tMin = Math.max(span.tMin, t); // entering
  else span.tMax = Math.min(span.tMax, t); // leaving
  return !(span.tMin > span.tMax);
}

export interface SightOptions {
  /**
   * Whether GM-only terrain counts. False for a player's own readout;
   * true for the GM's, and for anything that must reflect reality.
   */
  includeHidden?: boolean;
}

export interface CoverOptions extends SightOptions {
  /** The target's own height in metres. Defaults to a human. */
  targetHeight?: number;
  /** The ground the target is standing on, so a trench or a crate counts. */
  targetFeetY?: number;
  /**
   * COVER NEAR THE TARGET (the table ruling `coverNearTarget`, 2 m by default): a solid gives a level and
   * AP only when the line leaves it within this many metres of the target, measured along the ground to
   * the target's footprint edge. Farther solids still add resistance and still block sight - a berm at
   * the shooter's feet hides nobody 6 m away. Null or absent: anywhere on the line counts (the old rule).
   */
  coverReach?: number | null;
  /** The target's footprint, a radius in metres: "near the target" is measured to its edge. */
  targetRadius?: number;
}

/** A solid past the cover reach that would otherwise have given cover, for the readout's "why". */
export interface BeyondReach {
  /** Its label, or null when it has none. */
  source: string | null;
  /** How far from the target's footprint edge the line leaves it, in metres. */
  metres: number;
}

/** Does this solid block sight? Opaque by the one transparency rule the display draws by too. */
function blocksSight(solid: Solid): boolean {
  return solidTransparency(solid) === 0;
}

/**
 * What cover THIS solid grants THIS target.
 *
 * A solid whose level was written down keeps it. One marked 'derived'
 * works it out from how much of the target it hides - which is why cover
 * cannot be precomputed at all: the same wall answers differently for a
 * gaunt and a Dreadnought.
 *
 * Translucent material is the exception in the other direction: you can
 * see through it but cannot aim through it, so it grants full cover
 * however short it is.
 */
/**
 * How much material the round has to get through before it stops.
 *
 * In HARDNESS-METRES: a metre of hardness 8 is eight. Opaque material
 * blocks by sight long before this matters, so in practice it decides
 * only the see-through cases - which is exactly the question a 35 m
 * glass tower raised. Smoke has hardness 0 and therefore never stops a
 * round, however deep it is, which is right.
 */
export const PENETRATION_LIMIT = 60;

/**
 * Cover AP follows the DEPTH crossed, bounded either way.
 *
 * A pane of glass is not a wall and a wall is not a bunker, but the
 * clamp keeps this from running away: half hardness at its thinnest,
 * double at its thickest, and a ONE-METRE wall is exactly what it always
 * was - which is the balance every existing number was set against.
 */
function apForDepth(hardness: number, depth: number): number {
  return Math.round(hardness * Math.min(Math.max(depth, 0.5), 2));
}

function coverFromSolid(solid: Solid, targetHeight: number, targetFeetY: number, depth = 1): Cover {
  const ap = apForDepth(solid.cover?.ap ?? 0, depth);
  if (solid.transparency === 1) return { level: 'full', ap };
  if (solid.coverMode !== 'derived') return { level: solid.cover?.level ?? 'none', ap };
  const top = boxOf(solid).max.y;
  return { level: deriveCoverLevel(top - targetFeetY, targetHeight), ap };
}

/**
 * Solids the segment actually crosses, with their geometry.
 *
 * A solid containing either endpoint is skipped: a token standing on (or
 * inside) a piece of scenery is not hiding behind it, and counting it
 * would blind everyone standing on a rooftop.
 */
interface Crossing {
  solid: Solid;
  /** How far the line travels INSIDE it, in metres. */
  depth: number;
  /** Where along a→b the line goes INTO it, in [0, 1]: which solid is nearest the start. */
  tEntry: number;
  /** Where along a→b the line comes OUT of it, in [0, 1]: how near the target end it stands. */
  tExit: number;
}

function crossings(a: Vec3, b: Vec3, solids: Solid[], options: SightOptions = {}): Crossing[] {
  const includeHidden = options.includeHidden !== false;
  const span = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  const line = segmentBox(a, b);
  const out: Crossing[] = [];
  for (const solid of solids) {
    const crossing = mayCross(solid, line, includeHidden) ? crossingOf(solid, a, b, span) : null;
    if (crossing) out.push(crossing);
  }
  return out;
}

/**
 * Worth clipping at all? GM-only terrain is skipped for a player. BROADPHASE (2026-10-26): a segment whose box
 * misses the solid's box cannot cross it - the graded reach asks tiles x threats lines over every solid, and
 * this skips the face clipping for nearly all of them.
 */
function mayCross(solid: Solid, line: Box, includeHidden: boolean): boolean {
  return !(solid.hidden && !includeHidden) && boxesOverlap(line, boxOf(solid));
}

type Box = ReturnType<typeof boundingBox>;

/** Each solid's box, once: geometry is memoised per solid already, its box was rebuilt on every line. */
const boxCache = new WeakMap<Solid, Box>();

function boxOf(solid: Solid): Box {
  const hit = boxCache.get(solid);
  if (hit) return hit;
  const box = boundingBox(solidGeometry(solid));
  boxCache.set(solid, box);
  return box;
}

function segmentBox(a: Vec3, b: Vec3): Box {
  return {
    min: { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), z: Math.min(a.z, b.z) },
    max: { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y), z: Math.max(a.z, b.z) },
  };
}

/** Do two boxes share any point? A margin of EPSILON so a line grazing a face is never skipped. */
function boxesOverlap(p: Box, q: Box): boolean {
  return (
    p.min.x <= q.max.x + EPSILON &&
    q.min.x <= p.max.x + EPSILON &&
    p.min.y <= q.max.y + EPSILON &&
    q.min.y <= p.max.y + EPSILON &&
    p.min.z <= q.max.z + EPSILON &&
    q.min.z <= p.max.z + EPSILON
  );
}

/**
 * How much of this solid the line passes through, or null when it misses
 * or starts or ends inside it. The clip already knew where the line went
 * in and came out; it was simply throwing both away.
 */
function crossingOf(solid: Solid, a: Vec3, b: Vec3, span: number): Crossing | null {
  const g = solidGeometry(solid);
  if (containsPoint(g, a) || containsPoint(g, b)) return null;
  const hit = segmentHitsSolid(a, b, g);
  return hit ? { solid, depth: (hit.tMax - hit.tMin) * span, tEntry: hit.tMin, tExit: hit.tMax } : null;
}

/** Does anything OPAQUE stand between these two points? */
export function blocksLineOfSight(a: Vec3, b: Vec3, solids: Solid[], options: SightOptions = {}): boolean {
  return crossings(a, b, solids, options).some((c) => blocksSight(c.solid));
}

/** The first OPAQUE solid between these two points (nearest `a`), or null: what hides one from the other. */
export function firstBlocker(a: Vec3, b: Vec3, solids: Solid[], options: SightOptions = {}): Solid | null {
  const opaque = crossings(a, b, solids, options).filter((c) => blocksSight(c.solid));
  return opaque.sort((x, y) => x.tEntry - y.tEntry)[0]?.solid ?? null;
}

export interface CoverResult {
  /**
   * How much material the round must cross, in HARDNESS-METRES.
   *
   * Past `PENETRATION_LIMIT` there is no shot. This is what answers "why
   * does a 35 m glass tower only give cover?" - now it does not.
   */
  resistance: number;
  /**
   * How many things in the way grant cover. The highest of them applies;
   * they are never added together.
   */
  granting: number;
  /** The highest cover crossed - Deathwatch does not stack cover. */
  cover: Cover;
  /** Its label, for the readout. Null when nothing gives cover. */
  source: string | null;
  /** Everything crossed, in map order, whether it gives cover or not. */
  crossed: Solid[];
  /** True if any of it is opaque. */
  blocked: boolean;
  /** Solids that would have given cover but stand past the cover reach, nearest first. */
  beyond: BeyondReach[];
}

/**
 * What cover the target gets from the terrain between the two points.
 *
 * The HIGHEST value wins rather than the sum: two low walls are still
 * one low wall's worth of protection, and a rule that added them would
 * make a cluttered map absurd.
 */
export function coverAlong(a: Vec3, b: Vec3, solids: Solid[], options: CoverOptions = {}): CoverResult {
  const crossed = crossings(a, b, solids, options);
  const reach = reachOf(a, b, options);
  const tally = tallyCover(crossed, options.targetHeight ?? DEFAULT_TARGET_HEIGHT, options.targetFeetY ?? 0, reach);
  // Blocked by SIGHT, or by sheer depth of material. The second only ever
  // decides the see-through cases, because anything opaque has already
  // closed the shot by the first.
  const blocked = crossed.some((c) => blocksSight(c.solid)) || tally.resistance >= PENETRATION_LIMIT;
  return { ...tally, crossed: crossed.map((c) => c.solid), blocked };
}

/**
 * The best cover along the line, where it came from, how many things
 * granted any, and how much material was crossed.
 *
 * COVER IS NOT CUMULATIVE: the highest one applies and the rest are
 * ignored, because two low walls are one low wall's worth of protection
 * and adding them up would make a cluttered map absurd. That rule was
 * invisible until the readout could say "the highest of three" - hence
 * `granting`.
 *
 * HOW MUCH MATERIAL, as against how many things. Cover is not cumulative;
 * penetration is - a round crossing three metres of glass has crossed
 * three metres of glass whichever way it was built.
 */
type Tally = Pick<CoverResult, 'cover' | 'source' | 'granting' | 'resistance' | 'beyond'>;

function tallyCover(crossed: Crossing[], targetHeight: number, targetFeetY: number, reach: Reach | null): Tally {
  const tally: Tally = { cover: { level: 'none', ap: 0 }, source: null, granting: 0, resistance: 0, beyond: [] };
  for (const crossing of crossed) tallyOne(tally, crossing, targetHeight, targetFeetY, reach);
  tally.beyond.sort((x, y) => x.metres - y.metres);
  return tally;
}

/** One solid into the tally: its material always, its cover only when it stands near enough the target. */
function tallyOne(tally: Tally, { solid, depth, tExit }: Crossing, height: number, feetY: number, reach: Reach | null) {
  tally.resistance += (solid.cover?.ap ?? 0) * depth;
  const cover = coverFromSolid(solid, height, feetY, depth);
  if (cover.level === 'none') return;
  const far = pastReach(solid, tExit, reach);
  if (far !== null) {
    tally.beyond.push({ source: solid.label ?? null, metres: far });
    return;
  }
  tally.granting++;
  if (COVER_ORDER[cover.level] > COVER_ORDER[tally.cover.level]) {
    tally.cover = cover;
    tally.source = solid.label ?? null;
  }
}

/** The cover reach for one line: the limit, the line's length on the ground, and the target's half-footprint. */
interface Reach {
  limit: number;
  groundSpan: number;
  targetRadius: number;
}

function reachOf(a: Vec3, b: Vec3, options: CoverOptions): Reach | null {
  if (options.coverReach == null) return null;
  return {
    limit: options.coverReach,
    groundSpan: Math.hypot(b.x - a.x, b.z - a.z),
    targetRadius: options.targetRadius ?? 0,
  };
}

/**
 * How far past the cover reach this solid stands, as its distance from the target, or null when it counts.
 * Smoke and other see-through concealment hide you wherever they hang (the user, 2026-10-25): the rule is
 * about what physically stops a round, so translucent solids are exempt.
 */
function pastReach(solid: Solid, tExit: number, reach: Reach | null): number | null {
  if (!reach || solidTransparency(solid) === 1) return null;
  // From the target's footprint edge to where the line leaves the solid, along the ground. Both ends of the cover
  // line are lowered by the same amount, so its ground shadow is the shooter-to-target ground line.
  const metres = Math.max(0, (1 - tExit) * reach.groundSpan - reach.targetRadius);
  return metres > reach.limit ? metres : null;
}
